import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import type {
  DispatchableAction,
  RootAction,
  ThunkResult,
} from '../../types/redux';
import * as Actions from './actions';
import * as Selectors from './selectors';
import * as Thunks from './thunks';
import * as Types from './types';

// Hook that simplifies dispatching redux view part actions, by binding them
// to a specific view part reference.
export function useViewAction<
  RefType extends Types.AllPartRefs,
  ArgType extends any[],
>(ref: RefType, fn: (ref: RefType, ...fnArgs: ArgType) => DispatchableAction) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => dispatch(fn(ref, ...args)),
    [dispatch, ref, fn]
  );
}

export function useMaybeViewAction<
  RefType extends Types.AllPartRefs,
  ArgType extends any[],
>(
  ref: RefType | null | undefined,
  fn: (ref: RefType, ...fnArgs: ArgType) => DispatchableAction
) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => (ref != null ? dispatch(fn(ref, ...args)) : void 0),
    [dispatch, ref, fn]
  );
}

// Like the above version, but binds all arguments. Useful when an action requires multiple refs.
export function useViewActionBindAll<
  RefType extends Types.AllPartRefs,
  ArgType extends any[],
>(
  fn: (ref: RefType, ...fnArgs: ArgType) => RootAction,
  ref: RefType,
  ...args: ArgType
) {
  const dispatch = useDispatch();
  return useCallback(() => {
    return dispatch(fn(ref, ...args));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ref, fn, ...args]);
}

// Hook that simplifies dispatching redux view actions on a nullable view ref.
export function useViewRefAction<
  RefType extends Types.ViewRef,
  ArgType extends any[],
>(ref: RefType | null, fn: (ref: RefType, ...fnArgs: ArgType) => RootAction) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => (ref != null ? dispatch(fn(ref, ...args)) : void 0),
    [dispatch, ref, fn]
  );
}

export function useViewRefThunk<
  RefType extends Types.ViewRef,
  ArgType extends any[],
  ThunkType extends ThunkResult<any>,
>(ref: RefType | null, fn: (ref: RefType, ...fnArgs: ArgType) => ThunkType) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => (ref != null ? dispatch(fn(ref, ...args)) : void 0),
    [dispatch, ref, fn]
  );
}

export function useObjectCopy<RefType extends Types.AllPartRefs>(ref: RefType) {
  const [copyRef, setCopyRef] = useState<RefType | null>(null);

  const dispatch = useDispatch();
  useEffect(() => {
    // Copy the part on mount
    const action = Actions.copyObject(ref);
    // copy object is used for the panel editor, and unfortunately copying a panel
    // can be expensive for two reasons:
    // 1. panels parts is stored is as single object, and we do an immutable update
    //    on it. Immutable updates start getting expensive when you have 100s of
    //    thousands of keys on an object.
    // 2. we have some expensive selectors (panelBankConfig/selectors.ts) that fire
    //    any time panels parts changes.
    // So we dispatch this after a set timeout, so that the PanelEditor can display
    // display immediately with a loader, while copy/dispatch/expensive rendering
    // happens.
    // We could optimize all those things, but for now we just optimize for showing
    // users some feedback as soon as possible.
    setTimeout(() => dispatch(action), 1);
    const newRef = action.payload.ref as RefType;
    setCopyRef(newRef);
    return () => {
      // Clean up on unmount
      setCopyRef(null);
      dispatch(Thunks.deleteObject(newRef));
    };
  }, [dispatch, ref]);

  // Becomes true once the copy action succeeds
  const partExists = useSelector(Selectors.makePartExistsSelector(copyRef));

  if (copyRef != null && partExists) {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-as-const
      copying: false as false,
      ref: copyRef,
    };
  }
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  return {copying: true as true};
}

// This could be more explicitly called useTemporaryWhole but that's
// verbose and ugly. So trying out useNewObjectRef to see how it feels.
export function useNewObjectRef<T extends Types.ObjType>(
  type: T,
  whole: Types.WholeFromType<T>
) {
  return useExistingOrNewObjectRef(type, whole);
}

export function useExistingOrNewObjectRef<T extends Types.ObjType>(
  type: T,
  whole: Types.WholeFromType<T>,
  existing?: Types.PartRefFromType<T>
) {
  const [ref, setRef] = useState<Types.PartRefFromType<T> | null>(null);

  const dispatch = useDispatch();
  useEffect(() => {
    if (existing != null) {
      return;
    }
    const wholeAndType = {
      type,
      whole,
    } as unknown as Types.AllWholeWithTypesHelper<T>;
    // Add the object on mount
    const action = Actions.addObject(wholeAndType);
    dispatch(action);
    const newRef = action.payload.ref as Types.PartRefFromType<T>;
    setRef(newRef);

    // Clean up on unmount
    return () => {
      dispatch(Thunks.deleteObject(newRef));
      setRef(null);
    };
  }, [dispatch, type, whole, existing]);

  // Becomes true once the copy action succeeds
  const partExists = useSelector(Selectors.makePartExistsSelector(ref));

  if (existing != null) {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-as-const
      ready: true as true,
      ref: existing,
    };
  }
  if (ref != null && partExists) {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-as-const
      ready: true as true,
      ref,
    };
  }
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  return {ready: false as false};
}

export function usePart<T extends Types.ObjType>(
  ref: Types.PartRefFromType<T>
) {
  const selector = useMemo(() => Selectors.makePartSelector(ref), [ref]);
  return useSelector(selector);
}

export function usePartMaybe<T extends Types.ObjType>(
  ref?: Types.PartRefFromType<T> | null
) {
  const selector = useMemo(
    () => (ref != null ? Selectors.makePartSelector(ref) : () => undefined),
    [ref]
  );
  return useSelector(selector);
}

export function useParts<T extends Types.ObjType>(
  refs: Array<Types.PartRefFromType<T>>
) {
  const selector = useMemo(() => Selectors.makePartsSelector(refs), [refs]);
  return useSelector(selector);
}

export function useOptionalPart<T extends Types.ObjType>(
  ref: Types.PartRefFromType<T> | undefined
) {
  const selector = useMemo(
    () => Selectors.makeOptionalPartSelector(ref),
    [ref]
  );
  return useSelector(selector);
}

export function usePartMapped<T extends Types.ObjType, R>(
  ref: Types.PartRefFromType<T>,
  map: (part: Types.PartFromType<T>) => R
) {
  const stateRef = useRef({});
  return useSelector(
    Selectors.makePartMappedSelector(
      ref,
      map as (t: any) => R,
      stateRef.current
    )
  );
}
